import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { ProgressBar } from 'components/ProgressBar';
import { getTrans } from 'modules/i18n';
import { getHoursAndMinutesLabel } from 'utils/date';
import { getCompletedPercentage, getProgressBarBaseColor } from '../utils';

interface Props {
  expectedMinutes: number;
  reportedMinutes: number;
  reportedReasonMinutes: number;
}

export const WorkedHoursProgressBar = ({
  expectedMinutes,
  reportedMinutes,
  reportedReasonMinutes
}: Props) => {
  const theme = useTheme();

  const {
    totalMinutes,
    completedPercentage,
    progressValueLabel,
    totalValueLabel,
    baseColor
  } = useMemo(() => {
    // Calculate adjusted time (reportedMinutes + reportedReasonMinutes)
    const adjustedMinutes = reportedMinutes + reportedReasonMinutes;

    // When there are overhours (reportedMinutes > expectedMinutes), we need to handle:
    // 1. Positive reportedReasonMinutes: represents the overhour amount to explain, not additional hours.
    //    In this case, "Ermittelt" should show reportedMinutes (total worked from shifts).
    // 2. Negative reportedReasonMinutes:
    //    - If reportedMinutes is significantly larger than expected (>= 1.3x): show reportedMinutes
    //      (the negative reason explains significant overhours, doesn't reduce actual worked time)
    //    - Otherwise: show adjusted time (reason reduces actual worked time)
    // For other cases, use adjusted time.
    const isOverhours = reportedMinutes > expectedMinutes;
    const isOverhoursWithPositiveReason =
      isOverhours && reportedReasonMinutes > 0;
    const isSignificantOverhours =
      isOverhours && reportedMinutes >= expectedMinutes * 1.3;
    const isOverhoursWithNegativeReason =
      isOverhours && reportedReasonMinutes < 0 && isSignificantOverhours;

    const actualWorkedMinutes =
      isOverhoursWithPositiveReason || isOverhoursWithNegativeReason
        ? reportedMinutes
        : adjustedMinutes;

    // For the bar visualization, use the maximum of expected and actual worked time
    const totalMinutes = Math.max(expectedMinutes, actualWorkedMinutes);
    const progressMinutes = Math.min(expectedMinutes, actualWorkedMinutes);
    const completedPercentage = getCompletedPercentage(
      progressMinutes,
      totalMinutes
    );

    // "Ermittelt" label shows total actual worked time
    // "Erwartet" label shows expected time
    const progressValueLabel = getHoursAndMinutesLabel(actualWorkedMinutes);
    const totalValueLabel = getHoursAndMinutesLabel(expectedMinutes);

    // Base color determined by actual worked hours vs expected
    const baseColor = getProgressBarBaseColor(
      theme,
      expectedMinutes,
      actualWorkedMinutes
    );

    return {
      totalMinutes,
      completedPercentage,
      progressValueLabel,
      totalValueLabel,
      baseColor
    };
  }, [theme, expectedMinutes, reportedMinutes, reportedReasonMinutes]);

  const additionalPercentage = useMemo(() => {
    // Buffer shows overhours covered by a reason (purple color)
    // For overhours with positive reason minutes: don't show buffer (reason is explanatory, not additional)
    // For overhours with negative reason minutes where there are significant overhours:
    //   show absolute value as buffer (overhours covered by reason)
    // For non-overhours with positive reason minutes: show as buffer (additional time beyond shifts)
    const isOverhours = reportedMinutes > expectedMinutes;
    const isOverhoursWithPositiveReason =
      isOverhours && reportedReasonMinutes > 0;
    const isSignificantOverhours =
      isOverhours && reportedMinutes >= expectedMinutes * 1.3;
    const isOverhoursWithNegativeReason =
      isOverhours && reportedReasonMinutes < 0 && isSignificantOverhours;

    let reasonMinutesForBuffer = 0;
    if (isOverhoursWithNegativeReason) {
      // Show absolute value of negative reason minutes as purple buffer
      reasonMinutesForBuffer = Math.abs(reportedReasonMinutes);
    } else if (!isOverhoursWithPositiveReason) {
      // For non-overhours cases, show positive reason minutes
      reasonMinutesForBuffer = Math.max(0, reportedReasonMinutes);
    }

    return getCompletedPercentage(reasonMinutesForBuffer, totalMinutes);
  }, [reportedReasonMinutes, reportedMinutes, expectedMinutes, totalMinutes]);

  return (
    <ProgressBar
      value={completedPercentage}
      bufferValue={additionalPercentage}
      baseColor={baseColor}
      progressLabel={getTrans('weekDetailsPage.progressBar.title1')}
      totalLabel={getTrans('weekDetailsPage.progressBar.title2')}
      progressValueLabel={progressValueLabel}
      totalValueLabel={totalValueLabel}
    />
  );
};
